import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Une erreur est survenue. Réessayez.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F7FB]">
      <div className="hidden lg:flex flex-col justify-between p-16 bg-[#1E3A5F] text-white relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
          <span className="text-[18px] font-bold tracking-tight">Questiomed</span>
        </Link>
        <div className="relative z-10">
          <p className="font-serif text-4xl leading-tight italic">
            "Enfin un outil qui me fait vraiment gagner du temps"
          </p>
          <p className="mt-6 text-sm text-white/60 tracking-widest uppercase">— Dr D., médecin généraliste</p>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at bottom right, #3A8FA5, transparent 60%)" }} />
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2.5 mb-8 lg:hidden">
              <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
              <span className="text-[18px] font-bold tracking-tight text-[#1E3A5F]">Questiomed</span>
            </Link>
            <h1 className="font-serif text-4xl mb-2">Mot de passe oublié</h1>
            <p className="text-[#5F758F]">Nous vous enverrons un lien de réinitialisation par email.</p>
          </div>

          {sent ? (
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-8 text-center space-y-3" data-testid="forgot-sent">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#EEF3F8] flex items-center justify-center">
                <EnvelopeSimple size={26} weight="duotone" className="text-[#3A8FA5]" />
              </div>
              <p className="font-serif text-xl text-[#1E3A5F]">Vérifiez votre boîte mail</p>
              <p className="text-sm text-[#5F758F] leading-relaxed">
                Si un compte correspond à <span className="font-medium text-[#1E3A5F]">{email}</span>, un email avec un lien sécurisé vient d'être envoyé.
              </p>
              <p className="text-xs text-[#5F758F] italic pt-2">Le lien expire dans 1 heure. Pensez à vérifier vos spams.</p>
              <div className="pt-4">
                <Link to="/login">
                  <Button variant="outline" className="rounded-full" data-testid="back-to-login">Retour à la connexion</Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Email</Label>
                <Input
                  id="email" type="email" required autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="mt-2 h-12 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                  data-testid="forgot-email"
                />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white" data-testid="forgot-submit">
                {busy ? "Envoi…" : "Envoyer le lien de réinitialisation"}
              </Button>
              <p className="text-sm text-center text-[#5F758F]">
                <Link to="/login" className="text-[#1E3A5F] underline underline-offset-4" data-testid="back-to-login">Revenir à la connexion</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
